// app/try/test/page.tsx
'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Timer } from 'lucide-react'
import { diagnosticItems, diagnosticMeta } from '@/data/diagnostic'
import { clearAllLeadFlags } from '@/lib/marketing/teaser-lead-flag'
import { StemRenderer } from '@/components/sat/StemRenderer'
import MathText from '@/components/MathText'
import CalculatorPanel from '@/components/sat/CalculatorPanel'
import SectionReviewPage from '@/components/sat/SectionReviewPage'
import { diagnosticCalculatorAllowed } from '@/lib/calculator/calculator-policy'

// The 20 questions and 25-minute duration come from src/data/diagnostic.ts,
// the same source that /api/teaser/score scores answers against. Previously
// this page hardcoded a 12-item placeholder set whose IDs did not match the
// scorer's expectations, so every user got a base score of ~950 regardless
// of how they answered. See git log + the scoreAnswers tests for the full
// trail.
const QUESTIONS = diagnosticItems
const TEST_MINUTES = diagnosticMeta.durationMin

export default function ShortDiagnostic() {
  const router = useRouter()
  const [idx, setIdx] = useState(0)
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [secondsLeft, setSecondsLeft] = useState(TEST_MINUTES * 60)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Mark-for-review, and the last-look screen before submitting. The real SAT
  // runner has had both for a while; the diagnostic let "Finish" submit
  // straight from the last question, so a skipped question was scored as wrong
  // with nothing ever saying so. Ids, not indexes — the order is fixed today
  // but an id survives any future shuffle.
  const [flagged, setFlagged] = useState<Set<string>>(new Set())
  const [reviewing, setReviewing] = useState(false)

  useEffect(() => {
    const t = setInterval(() => setSecondsLeft((s) => (s > 0 ? s - 1 : 0)), 1000)
    return () => clearInterval(t)
  }, [])

  useEffect(() => {
    if (secondsLeft === 0) submit()
  }, [secondsLeft])

  const current = QUESTIONS[idx]
  const pct = Math.round(((idx + 1) / QUESTIONS.length) * 100)
  const isLast = idx === QUESTIONS.length - 1

  function pick(v: string) {
    setAnswers((a) => ({ ...a, [current.id]: v }))
  }

  function toggleFlag() {
    setFlagged((f) => {
      const next = new Set(f)
      if (next.has(current.id)) next.delete(current.id)
      else next.add(current.id)
      return next
    })
  }

  /** The shape SectionReviewPage reads. The diagnostic is MCQ-only, so
   *  numericAnswer is never set. */
  const reviewItems = QUESTIONS.map((q, i) => ({
    id: q.id,
    orderIndex: i,
    choice: answers[q.id] ?? null,
    flagged: flagged.has(q.id),
  }))
  const unanswered = QUESTIONS.length - Object.keys(answers).length

  function next() {
    setIdx((i) => Math.min(i + 1, QUESTIONS.length - 1))
  }
  function prev() {
    setIdx((i) => Math.max(i - 1, 0))
  }

  async function submit() {
    if (submitting) return
    setSubmitting(true)
    setError(null)

    try {
      // --- score server-side ---
      const res = await fetch('/api/teaser/score', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers }),
        cache: 'no-store',
      })

      if (!res.ok) {
        const text = await res.text()
        throw new Error(`Scoring failed: ${res.status} ${text}`)
      }

      const teaser: { estLow: number; estHigh: number; strengths: string[]; weaknesses: string[] } =
        await res.json()

      // Persist both the scored result and the raw answers so results can be re-derived
      localStorage.setItem('teaserResult', JSON.stringify(teaser))
      localStorage.setItem('teaserAnswers', JSON.stringify(answers))

      // Drop any lead-submit flags from a prior attempt. Without
      // this, a retake that lands in the same score band would see
      // the stale "we emailed you" / "delayed" state from the old
      // attempt and never get prompted for their email on the new
      // one. Each attempt should start with a fresh capture flow.
      clearAllLeadFlags(window.localStorage)

      // --- promote to server so charts have data immediately ---
      try {
        const promoteRes = await fetch('/api/teaser/promote', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ teaser }),
        })
        if (!promoteRes.ok) {
          const promoteBody = await promoteRes.text()
          console.warn('[test] promote returned non-OK:', promoteRes.status, promoteBody)
        }
      } catch (e) {
        // non-fatal; results page can still use teaser fallback
        console.warn('[test] promote failed', e)
      }

      // --- go to full results ---
      router.replace('/try/results?full=1')
    } catch (e) {
      console.error('[test] submit failed', e)
      setError('Something went wrong scoring your test. Please try again.')
      setSubmitting(false)
    }
  }

  const mins = Math.floor(secondsLeft / 60)
    .toString()
    .padStart(2, '0')
  const secs = (secondsLeft % 60).toString().padStart(2, '0')

  return (
    <main className="min-h-dvh bg-background text-foreground">
      <div className="mx-auto max-w-4xl px-4 py-8">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Timer className="h-4 w-4 text-brand-ink" />
            <span>
              Time left: {mins}:{secs}
            </span>
          </div>
          <div className="w-48">
            <Progress value={pct} />
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-md border border-red-800 bg-red-100 dark:bg-red-950/60 px-4 py-3 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {reviewing ? (
          /* Same last-look screen the real SAT runner uses, so a student who
             takes the diagnostic and later sits a full test meets one flow, not
             two. It carries the count this was missing: "N unanswered". */
          <SectionReviewPage
            items={reviewItems}
            onSelect={(i) => {
              setIdx(i)
              setReviewing(false)
            }}
            onSubmit={submit}
            finishing={submitting}
            submitLabel={unanswered > 0 ? `Submit anyway — ${unanswered} blank` : 'See my score'}
          />
        ) : (
          <Card className="border-border/70 bg-card/60">
            <CardHeader>
              <CardTitle className="text-base text-foreground">
                Q{idx + 1} of {QUESTIONS.length}
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  · {sectionLabel(current.section)} · {current.topic}
                </span>
              </CardTitle>
            </CardHeader>

            <CardContent className="space-y-4">
              {/* Optional reading passage(s) */}
              {current.passage && (
                <div className="rounded-md border border-border bg-card/40 p-4 text-sm leading-relaxed text-foreground">
                  {current.passage}
                </div>
              )}
              {current.passage2 && (
                <div className="rounded-md border border-border bg-card/40 p-4 text-sm leading-relaxed text-foreground">
                  {current.passage2}
                </div>
              )}

              {/* Stem via StemRenderer: keeps the W&L __underline__ markup AND
                renders embedded tables / charts / diagrams for the data-driven
                (visual) diagnostic questions. */}
              <StemRenderer
                stem={current.stem}
                className="text-lg leading-relaxed text-foreground"
              />

              {/* Choices */}
              <div className="grid gap-2">
                {current.choices.map((c, i) => {
                  const key = ['A', 'B', 'C', 'D'][i] ?? String.fromCharCode(65 + i)
                  const chosen = answers[current.id] === key
                  return (
                    <button
                      key={key}
                      onClick={() => pick(key)}
                      aria-pressed={chosen}
                      className={`rounded-md border px-3 py-2 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60
                      ${
                        chosen
                          ? 'border-brand/60 bg-brand/10 text-foreground'
                          : 'border-border bg-card/40 text-foreground hover:bg-muted/60'
                      }`}
                    >
                      <span className="mr-2 font-medium text-muted-foreground">{key}.</span>
                      {/* The stem above already renders carets as superscripts
                        via StemRenderer; the choices did not, so one question
                        showed "x^2" above and x² below. The button has no
                        aria-label — its name comes from its content — and the
                        <sup> MathText emits carries role="img" + aria-label, so
                        the name reads "x to the power of negative 1" instead of
                        the flattened "x-1" ("x minus 1", a different value). */}
                      <MathText text={c} />
                    </button>
                  )
                })}
              </div>

              {/* Inline Desmos on every Math item — the digital SAT allows a
                calculator throughout Math. This used to honour a paper-SAT
                no-calculator split, which made the free diagnostic the least
                faithful test on a site selling realistic digital SAT practice.
                See diagnosticCalculatorAllowed. */}
              {diagnosticCalculatorAllowed(current) && <CalculatorPanel className="pt-1" />}

              {/* Nav */}
              <div className="mt-4 flex items-center justify-between">
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="border-border text-foreground"
                    onClick={prev}
                    disabled={idx === 0 || submitting}
                  >
                    Back
                  </Button>
                  <Button
                    variant="outline"
                    className="border-border text-foreground"
                    onClick={toggleFlag}
                    disabled={submitting}
                    aria-pressed={flagged.has(current.id)}
                  >
                    {flagged.has(current.id) ? 'Marked for review' : 'Mark for review'}
                  </Button>
                </div>

                <Button
                  className="bg-brand text-on-brand"
                  onClick={isLast ? () => setReviewing(true) : next}
                  disabled={submitting}
                >
                  {/* On the last question this opens the review screen instead of
                    submitting. Submitting is now a deliberate second action. */}
                  {submitting ? 'Finishing...' : isLast ? 'Review' : 'Next'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </main>
  )
}

function sectionLabel(section: (typeof diagnosticItems)[number]['section']): string {
  switch (section) {
    // The digital SAT has TWO sections, not the paper test's four. The four
    // keys below are a legacy scoring grouping; a taker should never see them.
    case 'reading':
    case 'writing':
      return 'Reading and Writing'
    case 'math_nc':
    case 'math_calc':
      return 'Math'
  }
}
